/**
 * TemplateGallery - Modal for selecting board templates
 *
 * Templates are predefined node layouts for common workflows:
 * - Brainstorming: Central idea with radiating sticky notes
 * - Pros & Cons: Two-column comparison layout
 * - Meeting Notes: Agenda, notes, action items sections
 * - Research Flow: Question -> Sources -> Findings workflow
 *
 * How it works:
 * 1. User clicks "Templates" button in NodeToolbar
 * 2. Modal opens showing available templates
 * 3. User clicks a template card
 * 4. Template nodes are added to the Yjs map
 * 5. Modal closes automatically
 *
 * Templates don't clear existing nodes - they ADD to the canvas.
 * This allows users to combine templates or add to existing work.
 *
 * Each template is defined as an array of node specifications.
 * When applied, each node gets a unique ID via nanoid().
 */
import { useCallback } from 'react';
import { nanoid } from 'nanoid';
import type { Node } from '@xyflow/react';
import type * as Y from 'yjs';
import { STICKY_COLORS } from '../nodes/StickyNoteNode';
import { SHAPE_COLORS } from '../nodes/ShapeNode';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Template - Definition of a board template
 *
 * @property id - Unique identifier for the template
 * @property name - Display name shown in gallery
 * @property description - Short description of the template's purpose
 * @property icon - Emoji icon for visual identification
 * @property nodes - Array of node specifications (without IDs)
 */
interface Template {
  id: string;
  name: string;
  description: string;
  icon: string;
  /** Node specifications without IDs - IDs are generated on apply */
  nodes: Omit<Node, 'id'>[];
}

// ============================================================================
// TEMPLATE DEFINITIONS
// ============================================================================

/**
 * TEMPLATES - Predefined board templates
 *
 * Each template contains an array of nodes with:
 * - type: Node type ('stickyNote', 'shape', etc.)
 * - position: { x, y } coordinates
 * - data: Node-specific data (text, color, dimensions, etc.)
 *
 * Coordinates are chosen to create visually appealing layouts.
 * Colors use the STICKY_COLORS and SHAPE_COLORS constants.
 */
const TEMPLATES: Template[] = [
  // -------------------------------------------------------------------------
  // BRAINSTORMING TEMPLATE
  // Central idea with surrounding thoughts in different colors
  // -------------------------------------------------------------------------
  {
    id: 'brainstorming',
    name: 'Brainstorming',
    description: 'Central idea with surrounding thoughts',
    icon: '💡',
    nodes: [
      // Central "Main Idea" note - yellow, larger
      {
        type: 'stickyNote',
        position: { x: 400, y: 300 },
        data: { text: 'Main Idea', color: STICKY_COLORS.yellow, width: 200, height: 150 },
      },
      // Top-left idea - orange
      {
        type: 'stickyNote',
        position: { x: 150, y: 150 },
        data: { text: 'Idea 1', color: STICKY_COLORS.orange, width: 150, height: 100 },
      },
      // Top-right idea - green
      {
        type: 'stickyNote',
        position: { x: 650, y: 150 },
        data: { text: 'Idea 2', color: STICKY_COLORS.green, width: 150, height: 100 },
      },
      // Bottom-left idea - blue
      {
        type: 'stickyNote',
        position: { x: 150, y: 450 },
        data: { text: 'Idea 3', color: STICKY_COLORS.blue, width: 150, height: 100 },
      },
      // Bottom-right idea - purple
      {
        type: 'stickyNote',
        position: { x: 650, y: 450 },
        data: { text: 'Idea 4', color: STICKY_COLORS.purple, width: 150, height: 100 },
      },
    ],
  },

  // -------------------------------------------------------------------------
  // PROS & CONS TEMPLATE
  // Two-column comparison layout (green for pros, red for cons)
  // -------------------------------------------------------------------------
  {
    id: 'pros-cons',
    name: 'Pros & Cons',
    description: 'Two-column comparison layout',
    icon: '⚖️',
    nodes: [
      // PROS header - green
      {
        type: 'stickyNote',
        position: { x: 100, y: 50 },
        data: { text: 'PROS', color: '#bbf7d0', width: 300, height: 50 },
      },
      // Pro point 1
      {
        type: 'stickyNote',
        position: { x: 100, y: 120 },
        data: { text: '+ Point 1', color: '#bbf7d0', width: 300, height: 60 },
      },
      // Pro point 2
      {
        type: 'stickyNote',
        position: { x: 100, y: 200 },
        data: { text: '+ Point 2', color: '#bbf7d0', width: 300, height: 60 },
      },
      // Pro point 3
      {
        type: 'stickyNote',
        position: { x: 100, y: 280 },
        data: { text: '+ Point 3', color: '#bbf7d0', width: 300, height: 60 },
      },
      // CONS header - red
      {
        type: 'stickyNote',
        position: { x: 500, y: 50 },
        data: { text: 'CONS', color: '#fca5a5', width: 300, height: 50 },
      },
      // Con point 1
      {
        type: 'stickyNote',
        position: { x: 500, y: 120 },
        data: { text: '- Point 1', color: '#fca5a5', width: 300, height: 60 },
      },
      // Con point 2
      {
        type: 'stickyNote',
        position: { x: 500, y: 200 },
        data: { text: '- Point 2', color: '#fca5a5', width: 300, height: 60 },
      },
      // Con point 3
      {
        type: 'stickyNote',
        position: { x: 500, y: 280 },
        data: { text: '- Point 3', color: '#fca5a5', width: 300, height: 60 },
      },
    ],
  },

  // -------------------------------------------------------------------------
  // MEETING NOTES TEMPLATE
  // Structured layout for meeting documentation
  // -------------------------------------------------------------------------
  {
    id: 'meeting-notes',
    name: 'Meeting Notes',
    description: 'Agenda, notes, and action items',
    icon: '📋',
    nodes: [
      // Meeting info header - blue
      {
        type: 'stickyNote',
        position: { x: 100, y: 50 },
        data: {
          text: '📅 Meeting: [Date]\n\nAttendees:\n- Name 1\n- Name 2',
          color: STICKY_COLORS.blue,
          width: 250,
          height: 140,
        },
      },
      // Agenda section - yellow
      {
        type: 'stickyNote',
        position: { x: 100, y: 220 },
        data: {
          text: '📝 Agenda\n\n1. Topic 1\n2. Topic 2\n3. Topic 3',
          color: STICKY_COLORS.yellow,
          width: 250,
          height: 180,
        },
      },
      // Notes section - green
      {
        type: 'stickyNote',
        position: { x: 400, y: 220 },
        data: {
          text: '💬 Notes\n\n• Key point\n• Discussion\n• Decision',
          color: STICKY_COLORS.green,
          width: 250,
          height: 180,
        },
      },
      // Action items section - orange
      {
        type: 'stickyNote',
        position: { x: 700, y: 220 },
        data: {
          text: '✅ Action Items\n\n[ ] Task 1 - Owner\n[ ] Task 2 - Owner\n[ ] Task 3 - Owner',
          color: STICKY_COLORS.orange,
          width: 250,
          height: 180,
        },
      },
    ],
  },

  // -------------------------------------------------------------------------
  // RESEARCH FLOW TEMPLATE
  // Left-to-right workflow: Question -> Sources -> Findings
  // -------------------------------------------------------------------------
  {
    id: 'research',
    name: 'Research Flow',
    description: 'Question -> Research -> Findings workflow',
    icon: '🔍',
    nodes: [
      // Research question - purple (start)
      {
        type: 'stickyNote',
        position: { x: 100, y: 200 },
        data: {
          text: '❓ Research Question\n\nWhat do I want to learn?',
          color: STICKY_COLORS.purple,
          width: 200,
          height: 150,
        },
      },
      // Arrow indicator - triangle shape pointing right
      {
        type: 'shape',
        position: { x: 320, y: 250 },
        data: { shapeType: 'triangle', color: SHAPE_COLORS.gray, width: 40, height: 40 },
      },
      // Sources section - blue (middle)
      {
        type: 'stickyNote',
        position: { x: 390, y: 200 },
        data: {
          text: '📚 Sources\n\n1. Source 1\n2. Source 2\n3. Source 3',
          color: STICKY_COLORS.blue,
          width: 200,
          height: 150,
        },
      },
      // Arrow indicator - triangle shape pointing right
      {
        type: 'shape',
        position: { x: 610, y: 250 },
        data: { shapeType: 'triangle', color: SHAPE_COLORS.gray, width: 40, height: 40 },
      },
      // Key findings - green (end)
      {
        type: 'stickyNote',
        position: { x: 680, y: 200 },
        data: {
          text: '💡 Key Findings\n\n• Finding 1\n• Finding 2\n• Finding 3',
          color: STICKY_COLORS.green,
          width: 200,
          height: 150,
        },
      },
    ],
  },
];

// ============================================================================
// TEMPLATE ICONS
// ============================================================================

function TemplateIcon({ templateId }: { templateId: string }) {
  const baseProps = {
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };

  switch (templateId) {
    case 'brainstorming':
      return (
        <svg {...baseProps}>
          <path d="M12 3a6 6 0 0 0-3.5 10.9V17a1 1 0 0 0 1 1h5a1 1 0 0 0 1-1v-3.1A6 6 0 0 0 12 3z" />
          <path d="M9 21h6" />
          <path d="M10 17h4" />
        </svg>
      );
    case 'pros-cons':
      return (
        <svg {...baseProps}>
          <rect x="4" y="5" width="7" height="14" rx="1" />
          <rect x="13" y="5" width="7" height="14" rx="1" />
          <path d="M7.5 9h0.01" />
          <path d="M16.5 9h0.01" />
        </svg>
      );
    case 'meeting-notes':
      return (
        <svg {...baseProps}>
          <rect x="5" y="4" width="14" height="16" rx="2" />
          <path d="M8 8h8" />
          <path d="M8 12h8" />
          <path d="M8 16h5" />
        </svg>
      );
    case 'research':
      return (
        <svg {...baseProps}>
          <circle cx="11" cy="11" r="5" />
          <path d="M15 15l4 4" />
        </svg>
      );
    default:
      return (
        <svg {...baseProps}>
          <rect x="4" y="4" width="16" height="16" rx="3" />
        </svg>
      );
  }
}

// ============================================================================
// COMPONENT PROPS
// ============================================================================

/**
 * TemplateGalleryProps - Props for the TemplateGallery component
 *
 * @property isOpen - Whether the modal is visible
 * @property onClose - Callback to close the modal
 * @property nodesMap - Yjs map for adding template nodes
 * @property makeChange - Wrapper for undo/redo support
 */
interface TemplateGalleryProps {
  isOpen: boolean;
  onClose: () => void;
  nodesMap: Y.Map<Node>;
  makeChange: (fn: () => void) => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * TemplateGallery - Modal component for selecting board templates
 *
 * Usage:
 * ```tsx
 * <TemplateGallery
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   nodesMap={nodesMap}
 *   makeChange={makeChange}
 * />
 * ```
 */
export function TemplateGallery({
  isOpen,
  onClose,
  nodesMap,
  makeChange,
}: TemplateGalleryProps) {
  // ---------------------------------------------------------------------------
  // APPLY TEMPLATE HANDLER
  // ---------------------------------------------------------------------------
  /**
   * applyTemplate - Add template nodes to the canvas
   *
   * Process:
   * 1. Generate unique ID for each node (nanoid)
   * 2. Add all nodes to Yjs map in a single transaction
   * 3. Close the modal
   *
   * The makeChange wrapper ensures:
   * - All nodes are added atomically
   * - Undo reverts the entire template
   */
  const applyTemplate = useCallback(
    (template: Template) => {
      makeChange(() => {
        // Add all nodes from template with unique IDs
        for (const nodeTemplate of template.nodes) {
          const id = nanoid();
          const node: Node = {
            ...nodeTemplate,
            id,
          };
          nodesMap.set(id, node);
        }
      });

      // Close modal after applying
      onClose();
    },
    [nodesMap, makeChange, onClose]
  );

  // ---------------------------------------------------------------------------
  // RENDER (nothing if not open)
  // ---------------------------------------------------------------------------
  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* ===================================================================
          BACKDROP
          Semi-transparent overlay that closes modal when clicked
          =================================================================== */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-label="Close modal"
      />

      {/* ===================================================================
          MODAL CONTAINER
          White panel with template grid
          =================================================================== */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
        {/* -----------------------------------------------------------------
            HEADER
            Title and close button
            ----------------------------------------------------------------- */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Choose a Template
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* -----------------------------------------------------------------
            TEMPLATE GRID
            2-column grid of template cards
            ----------------------------------------------------------------- */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="grid grid-cols-2 gap-4">
            {TEMPLATES.map((template) => (
              <button
                key={template.id}
                onClick={() => applyTemplate(template)}
                className="flex flex-col items-start p-4 rounded-lg border-2 border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
              >
                {/* Template icon */}
                <div
                  className="mb-2 flex items-center justify-center"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: 'var(--color-surface-muted)',
                    color: 'var(--color-text-secondary)',
                  }}
                >
                  <TemplateIcon templateId={template.id} />
                </div>

                {/* Template name */}
                <h3 className="font-semibold text-gray-900">
                  {template.name}
                </h3>

                {/* Template description */}
                <p className="text-sm text-gray-500 mt-1">
                  {template.description}
                </p>

                {/* Element count */}
                <span className="text-xs text-gray-400 mt-2">
                  {template.nodes.length} elements
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* -----------------------------------------------------------------
            FOOTER
            Informational text about template behavior
            ----------------------------------------------------------------- */}
        <div className="px-6 py-4 border-t bg-gray-50 text-center text-sm text-gray-500">
          Templates add nodes to your current board
        </div>
      </div>
    </div>
  );
}
